import { Autocomplete, TextField } from '@mui/material';
import { useStates } from '@/api/entities';

/**
 * State / province picker driven by the geography master
 * (GET /api/ref/states). Renders a searchable Autocomplete of
 * `Name (CODE)` for countries that have a master list (IN, AE, GB, US);
 * for any other country — or before a country is chosen — it degrades to
 * a free-text uppercase code field so data entry is never blocked.
 *
 * Stores the ISO 3166-2 short code (e.g. "MH"), which is what the PT/LWF
 * engine keys on.
 */
export type StateSelectProps = {
  countryCode: string | null | undefined;
  value: string | null;
  onChange: (code: string | null) => void;
  label?: string;
  size?: 'small' | 'medium';
  helperText?: string;
  required?: boolean;
  sx?: object;
  fullWidth?: boolean;
};

export function StateSelect({
  countryCode, value, onChange,
  label = 'State / Province',
  size = 'small',
  helperText,
  required,
  sx,
  fullWidth = true,
}: StateSelectProps) {
  const { data: states = [] } = useStates(countryCode);

  // No master list for this country → free-text uppercase code entry.
  if (!countryCode || states.length === 0) {
    return (
      <TextField
        label={label}
        required={required}
        fullWidth={fullWidth}
        size={size}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value.toUpperCase() || null)}
        inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'JetBrains Mono, ui-monospace, monospace' } }}
        helperText={helperText ?? 'State code'}
        sx={sx}
      />
    );
  }

  const selected = states.find((s) => s.code === value) ?? null;

  return (
    <Autocomplete
      size={size}
      fullWidth={fullWidth}
      sx={sx}
      options={states}
      value={selected}
      onChange={(_, opt) => onChange(opt?.code ?? null)}
      getOptionLabel={(o) => `${o.name} (${o.code})`}
      isOptionEqualToValue={(o, v) => o.code === v.code}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
        />
      )}
    />
  );
}
